/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/anchor-has-content */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import classNames from "classnames";
import find from "lodash/find";
import { ChatConversation } from "PFApp/components/chat/chat_conversation";
import ChatMenu from "PFApp/components/chat/chat_menu";
import { ChatParticipants } from "PFApp/components/chat/chat_participants";
import { LOADING, NORMAL, PARTICIPANTS } from "PFApp/components/chat/chat_states";
import InviteModal from "PFApp/conversations/invite/invite_modal";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { ActionIcon } from "PFComponents/action_icon";
import { Inline } from "PFComponents/containers";
import { Icon } from "PFComponents/icon";
import { ProfileAvatar } from "PFComponents/profile_avatar/profile_avatar";
import { getTargetType, getTargetUrl } from "PFCore/helpers/conversation";
import { getProfileName } from "PFCore/helpers/profile";
import { useForceUpdate } from "PFCore/helpers/use_force_update";
import useIsPermittedTo from "PFCore/helpers/use_is_permitted_to";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useChatInvalidate } from "PFCore/hooks/queries/chat/use_chat_invalidate";
import { followParticipant } from "PFCore/services/chat/follow_participant";
import { PermissionRule } from "PFTypes";
import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import css from "./chat.module.scss";
import { setChatSendOnEnter } from "./send_on_enter";

const preventEvent = (event) => {
  event?.preventDefault?.();
  event?.stopPropagation?.();
};

const getCurrentParticipant = (participants, conversation, currentProfile) =>
  find(participants, ({ profile }) => profile?.id === currentProfile.id) || {
    conversationId: conversation.id,
    profileId: currentProfile.id
  };

const Chat = ({
  chatState,
  conversation,
  currentProfile,
  emptyMessage,
  expandable = true,
  expanded,
  onClose,
  onExpand,
  onMinimize,
  onSelect,
  onUnMinimize,
  onLoadPreviousMessages,
  isBelow,
  messages,
  minimized,
  participants,
  selected,
  setChatState,
  style,
  flashTitle,
  isFetchingPreviousMessages
}) => {
  const { t } = useTranslation("core", { keyPrefix: "chat.conversation.subtitle" });
  const isPermittedTo = useIsPermittedTo(currentProfile);
  const { invalidateParticipants } = useChatInvalidate();

  const forceUpdate = useForceUpdate();
  const { showGrowl } = useGrowlStoreActions();
  const { getFirstError } = useResponseErrors();

  const [attachments, setAttachments] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [inviteModalShown, setInviteModalShown] = useState(false);
  const [currentParticipant, setCurrentParticipant] = useState(
    getCurrentParticipant(participants, conversation, currentProfile)
  );

  const dropdownFocusRef = useRef();

  useEffect(() => {
    dropdownFocusRef.current?.addEventListener("blur", () => setDropdownOpen(false));
  }, [chatState, messages, minimized]);

  useEffect(() => {
    dropdownFocusRef.current?.focus();
  }, []);

  useEffect(() => {
    setCurrentParticipant(getCurrentParticipant(participants, conversation, currentProfile));
  }, [participants]);

  const handleAddAttachment = useCallback((attachment) => {
    setAttachments((attachments) => [...attachments, attachment]);
  }, []);

  const handleRemoveAttachment = useCallback((attachment) => {
    setAttachments((attachments) => attachments.filter((other) => other !== attachment));
  }, []);

  const handleAttachmentSaveFailed = useCallback(
    (error) => {
      const errorMessage = getFirstError(error);
      // the fallback copy -> most likely a 500
      const message = errorMessage || "Your attachment upload failed. Please try again.";

      showGrowl({ message, kind: "error" });
    },
    [getFirstError, showGrowl]
  );

  const handleUnMinimize = useCallback(
    (event) => {
      preventEvent(event);
      onSelect?.(conversation.id);
      onUnMinimize?.(conversation.id);
    },
    [conversation.id, onSelect, onUnMinimize]
  );

  const handleExpand = useCallback(
    (event) => {
      preventEvent(event);
      onExpand?.(conversation.id, !expanded);
    },
    [conversation.id, expanded, onExpand]
  );

  const handleMinimize = useCallback(
    (event) => {
      preventEvent(event);
      onMinimize?.(conversation.id); // no handleMinimize when embeded in the page (rather then floating)
      setDropdownOpen(false);
    },
    [conversation.id, onMinimize]
  );

  const handleClose = useCallback(
    (event) => {
      preventEvent(event);
      onClose(conversation.id);
    },
    [conversation.id, onClose]
  );

  const handleClearAttachments = () => setAttachments([]);

  const showParticipants = useCallback(() => {
    setChatState(PARTICIPANTS);
  }, [setChatState]);

  let { title } = conversation;
  let isFollowing = false;
  let targetUrl = getTargetUrl(conversation || "");
  const isPrivate = conversation.kind === "normal";
  const { unreadCount } = conversation;

  // there will be only one other participant for now
  const otherParticipant = find(participants, ({ profile }) => profile.id !== currentProfile.id);
  const otherPerson = otherParticipant?.profile || null;

  if (chatState === LOADING) {
    title = "...";
  } else {
    if (getTargetType(conversation) === "Profile") {
      title = otherPerson ? getProfileName(otherPerson) : "";
      targetUrl = otherPerson ? `/profiles/${otherPerson.id}` : false;
    }

    isFollowing = currentParticipant.notificationState === "following";
  }

  return (
    <div
      className={classNames(css.root, {
        [css.minimized]: minimized,
        [css.selected]: selected,
        [css.expanded]: expanded
      })}
      style={style}
      onClick={() => onSelect(conversation.id)}
      data-qa-id="chat-root"
    >
      {inviteModalShown && (
        <InviteModal
          conversation={conversation}
          participants={participants}
          handleClose={() => setInviteModalShown(false)}
        />
      )}
      <div
        className={classNames(css.header, { [css.incognito]: isPrivate, [css.flashTitle]: flashTitle })}
        onClick={handleUnMinimize}
        data-qa-id="chat-window-header"
      >
        <div
          className={classNames(css.title, { [css.titleBelow]: !selected && isBelow })}
          data-qa-id="chat-window-title"
        >
          {selected && !minimized && (
            //this is the open chat header
            <Link to={targetUrl} className={css.wrapper}>
              {otherPerson && <ProfileAvatar profile={otherPerson} size={22} />}
              <div className={css.titleName}>{title}</div>
            </Link>
          )}
          {/* here the chat minimed is rendered */}
          {(!selected || (selected && minimized)) && <span>{title}</span>}
        </div>
        {minimized && (
          <span onClick={handleClose} className={css.minimizedActions}>
            {unreadCount > 0 && <div className={css.unreaded}>{unreadCount}</div>}
            <Icon name="cross" size="xxs" />
          </span>
        )}

        {selected && !minimized && (
          <Inline
            alignItems="center"
            justifyContent="end"
            className={css.headerActions}
            data-qa-id="chat-all-header"
          >
            <ActionIcon name="subtract" size="sm" disableHover onClick={handleMinimize} />
            {expandable &&
              (expanded ? (
                <ActionIcon name="collapse" size="sm" disableHover onClick={handleExpand} />
              ) : (
                <ActionIcon name="expand" size="sm" disableHover onClick={handleExpand} />
              ))}
            {dropdownOpen && (
              <ActionIcon
                name="menu-horizontal"
                size="sm"
                onClick={() => setDropdownOpen(false)}
                disableHover
              />
            )}
            {!dropdownOpen && (
              <ActionIcon
                name="menu-horizontal"
                size="sm"
                onClick={chatState === LOADING ? null : () => setDropdownOpen(true)}
                disableHover
              />
            )}
            <ActionIcon name="cross" size="xxs" onClick={handleClose} disableHover />
          </Inline>
        )}

        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
        <a ref={dropdownFocusRef} tabIndex={0} className={css.dropdownFocus} />
        {!minimized && dropdownOpen && (
          <ChatMenu
            isOwned={currentProfile.id === conversation.owner?.id}
            conversation={conversation}
            showParticipants={showParticipants}
            targetUrl={targetUrl}
            handleChange={(fn) => {
              setDropdownOpen(false);
              fn?.();
            }}
            setSendOnEnter={(val) => {
              setChatSendOnEnter(val);
            }}
            isFollowing={isFollowing}
            handleFollowingChange={(follow) => {
              followParticipant(currentParticipant.id, follow).then((resp) => {
                setCurrentParticipant(resp);
                invalidateParticipants(conversation.id, currentProfile.id);
              });
            }}
            handleSendOnEnter={(val) => {
              setChatSendOnEnter(val);
              forceUpdate();
            }}
            handleInviteToChat={() => setInviteModalShown(true)}
          />
        )}
      </div>
      {!minimized && (
        <div className={css.bodyWrap}>
          {(chatState === NORMAL || chatState === LOADING) && (
            <ChatConversation
              attachments={attachments}
              clearAttachments={handleClearAttachments}
              conversationId={conversation.id}
              currentProfile={currentProfile}
              emptyMessage={emptyMessage}
              isPrivate={isPrivate}
              loading={chatState === LOADING}
              messages={messages}
              onAttachmentRemove={handleRemoveAttachment}
              onAttachmentSaved={handleAddAttachment}
              onAttachmentSaveFailed={handleAttachmentSaveFailed}
              onLoadPreviousMessages={onLoadPreviousMessages}
              onMinimize={handleMinimize}
              onOpenParticipants={showParticipants}
              showMentionables={conversation.kind === "primary" && isPermittedTo(PermissionRule.ProfileList)}
              subtitle={isFollowing ? t("following") : t("notFollowing")}
              isFetchingPreviousMessages={isFetchingPreviousMessages}
            />
          )}
          {chatState === PARTICIPANTS && (
            <ChatParticipants
              conversationId={conversation.id}
              withMessage={conversation.kind === "primary"}
              ownerId={conversation.owner?.id}
              isPrivate={isPrivate}
              handleClose={() => setChatState(NORMAL)}
              handleMinimize={handleMinimize}
            />
          )}
        </div>
      )}
    </div>
  );
};

Chat.propTypes = {
  chatState: PropTypes.oneOf([LOADING, NORMAL, PARTICIPANTS]).isRequired,
  messages: PropTypes.any.isRequired,
  conversation: PropTypes.any.isRequired,
  participants: PropTypes.any.isRequired,
  selected: PropTypes.bool.isRequired,
  setChatState: PropTypes.func.isRequired,
  emptyMessage: PropTypes.string.isRequired,
  currentProfile: PropTypes.object.isRequired,
  flashTitle: PropTypes.bool,
  isFetchingPreviousMessages: PropTypes.bool,

  style: PropTypes.any,
  isBelow: PropTypes.bool,
  minimized: PropTypes.bool.isRequired,
  expanded: PropTypes.bool.isRequired,
  expandable: PropTypes.bool.isRequired,

  onSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  onMinimize: PropTypes.func,
  onExpand: PropTypes.func,
  onUnMinimize: PropTypes.func,
  onLoadPreviousMessages: PropTypes.func
};

export default Chat;
